/**
 * Security & audit overview (2026-09-29). Replaces a panel of hardcoded
 * "Enabled / 30 minutes / High" chips and invented audit events with the
 * workspace's real sign-in policy and its real recent audit trail
 * (backend: /auth-policies, /governance/audit-trail; events are written by
 * app/services/audit.py).
 */
import { useEffect, useState } from 'react'
import { ShieldCheck, ShieldAlert, RefreshCw } from 'lucide-react'

import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { enterpriseTrustService, type AuthPolicy } from '../../services/enterpriseTrust'
import { governanceSlaService, type AuditLogEntry } from '../../services/governanceSla'
import { useLocalization } from '../../contexts/LocalizationContext'
import { AUDIT_WARN_ACTIONS, actionLabel } from './auditLabels'

function PolicyRow({ label, value, good }: { label: string; value: string; good: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-border p-3">
      <span className="text-sm text-foreground">{label}</span>
      <span className={`rounded px-2 py-0.5 text-xs font-medium ${good ? 'bg-success/15 text-success' : 'bg-muted text-muted-foreground'}`}>
        {value}
      </span>
    </div>
  )
}

export function SecurityOverviewSection({ onEditPolicy }: { onEditPolicy: () => void }) {
  const { formatDate } = useLocalization()
  const [policy, setPolicy] = useState<AuthPolicy | null>(null)
  const [events, setEvents] = useState<AuditLogEntry[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = () => {
    setError(null)
    enterpriseTrustService.getAuthPolicy().then(setPolicy).catch(() => setError('Could not load the sign-in policy.'))
    governanceSlaService.getAuditTrail({ limit: 15 }).then(setEvents).catch(() => {
      setEvents([])
      setError('Could not load recent activity.')
    })
  }
  useEffect(load, [])

  const complexity = policy
    ? [policy.password_require_upper, policy.password_require_lower, policy.password_require_digit, policy.password_require_symbol].filter(Boolean).length
    : 0

  return (
    <div className="space-y-6">
      <div>
        <h1 className="mb-1 text-2xl text-foreground">Security &amp; audit</h1>
        <p className="text-muted-foreground">How people sign in to this workspace, and what has happened recently.</p>
      </div>
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              {policy?.mfa_required ? <ShieldCheck className="h-5 w-5 text-success" /> : <ShieldAlert className="h-5 w-5 text-warning" />}
              Sign-in policy
            </CardTitle>
            <CardDescription>Applies to everyone in the workspace.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {!policy ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : (
              <>
                <PolicyRow label="Two-factor sign-in" value={policy.mfa_required ? 'Required' : 'Optional'} good={policy.mfa_required} />
                <PolicyRow label="Sign out after" value={policy.session_timeout_minutes >= 60
                  ? `${Math.round(policy.session_timeout_minutes / 60)} hour(s)` : `${policy.session_timeout_minutes} minutes`}
                  good={policy.session_timeout_minutes <= 480} />
                <PolicyRow label="Passwords" value={`${policy.password_min_length}+ characters, ${complexity} of 4 character types`}
                  good={policy.password_min_length >= 12 && complexity >= 3} />
                <PolicyRow label="IP allowlist" value={policy.ip_allowlist?.length ? `${policy.ip_allowlist.length} range(s)` : 'Off'}
                  good={Boolean(policy.ip_allowlist?.length)} />
                <PolicyRow label="Password expiry" value={policy.password_rotation_days ? `Every ${policy.password_rotation_days} days` : 'Never'}
                  good={true} />
                <Button variant="outline" onClick={onEditPolicy} className="mt-2 min-h-[44px]">Change sign-in policy</Button>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-2">
            <div>
              <CardTitle className="text-base">Recent activity</CardTitle>
              <CardDescription>Sign-ins, user and permission changes, exports and public links.</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={load} aria-label="Refresh activity"><RefreshCw className="h-4 w-4" /></Button>
          </CardHeader>
          <CardContent>
            {events === null ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : events.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nothing recorded yet. Sign-ins and admin changes will appear here.</p>
            ) : (
              <ul className="space-y-1" aria-label="Recent audit events">
                {events.map((e) => (
                  <li key={e.id} className="flex items-start justify-between gap-3 rounded-md p-2 text-sm hover:bg-muted/50">
                    <span className="flex items-start gap-2">
                      <span aria-hidden className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${AUDIT_WARN_ACTIONS.has(e.action) ? 'bg-warning' : 'bg-primary'}`} />
                      <span>
                        <span className="block font-medium text-foreground">{actionLabel(e.action)}</span>
                        <span className="block text-xs text-muted-foreground">
                          {e.description ?? ''}{e.user_name ? ` · by ${e.user_name}` : ''}
                        </span>
                      </span>
                    </span>
                    <span className="shrink-0 text-xs text-muted-foreground">{formatDate(e.created_at, { withTime: true })}</span>
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-3 text-xs text-muted-foreground">
              The full tamper-evident log, with export, is under Enterprise Trust → Audit (Business plan).
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
